import { ChevronLeftIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-left'
import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { Button } from '@repo/studio-design-system-ui-button'

import { SheetClose, SheetContent, SheetRoot, SheetTrigger } from '../sheet'
import { SheetContentBoundary } from '../sheet-content-boundary'

interface BottomSheetNavigationContextValue {
  active: string | null
  canGoBack: boolean
  push: (name: string) => void
  pop: () => void
}

interface BottomSheetNavigationInternalValue extends BottomSheetNavigationContextValue {
  registerScreen: (name: string) => void
}

const BottomSheetNavigationContext = React.createContext<BottomSheetNavigationInternalValue | null>(
  null,
)

const BottomSheetScreenContext = React.createContext<string | null>(null)

function useBottomSheetNavigationInternal() {
  const ctx = React.useContext(BottomSheetNavigationContext)
  if (!ctx) {
    throw new Error('BottomSheet navigation hooks and Screen must be used within BottomSheet.Root')
  }
  return ctx
}

export function useBottomSheetNavigation(): BottomSheetNavigationContextValue {
  return useBottomSheetNavigationInternal()
}

function BottomSheetNavigationProvider({
  children,
  stack,
  setStack,
  screenOrderRef,
  onResetStack,
}: {
  children: React.ReactNode
  stack: string[]
  setStack: React.Dispatch<React.SetStateAction<string[]>>
  screenOrderRef: React.MutableRefObject<string[]>
  onResetStack: React.MutableRefObject<() => void>
}) {
  const active = stack.at(-1) ?? null
  const canGoBack = stack.length > 1

  const registerScreen = React.useCallback(
    (name: string) => {
      if (screenOrderRef.current.includes(name)) {
        return
      }
      screenOrderRef.current = [...screenOrderRef.current, name]
      setStack(current => (current.length === 0 ? [name] : current))
    },
    [screenOrderRef, setStack],
  )

  const push = React.useCallback(
    (name: string) => {
      if (name === active) {
        return
      }
      if (!screenOrderRef.current.includes(name)) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(`BottomSheet: unknown screen "${name}"`)
        }
        return
      }
      setStack(current => [...current, name])
    },
    [active, screenOrderRef, setStack],
  )

  const pop = React.useCallback(() => {
    setStack(current => {
      if (current.length <= 1) {
        return current
      }
      return current.slice(0, -1)
    })
  }, [setStack])

  const resetStack = React.useCallback(() => {
    const first = screenOrderRef.current[0]
    setStack(first ? [first] : [])
  }, [screenOrderRef, setStack])

  onResetStack.current = resetStack

  const value = React.useMemo<BottomSheetNavigationInternalValue>(
    () => ({
      active,
      canGoBack,
      push,
      pop,
      registerScreen,
    }),
    [active, canGoBack, push, pop, registerScreen],
  )

  return (
    <BottomSheetNavigationContext.Provider value={value}>
      {children}
    </BottomSheetNavigationContext.Provider>
  )
}

function Root({
  open,
  onOpenChange,
  modal = true,
  children,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  children: React.ReactNode
}) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [stack, setStack] = React.useState<string[]>([])
  const screenOrderRef = React.useRef<string[]>([])
  const resetStackRef = React.useRef<() => void>(() => {})

  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetStackRef.current()
      }
      if (!isControlled) {
        setInternalOpen(newOpen)
      }
      onOpenChange?.(newOpen)
    },
    [isControlled, onOpenChange],
  )

  React.useEffect(() => {
    if (!isOpen) {
      resetStackRef.current()
    }
  }, [isOpen])

  return (
    <BottomSheetNavigationProvider
      stack={stack}
      setStack={setStack}
      screenOrderRef={screenOrderRef}
      onResetStack={resetStackRef}
    >
      <SheetRoot open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
        {children}
      </SheetRoot>
    </BottomSheetNavigationProvider>
  )
}

function Trigger({ children }: { children: React.ReactNode }) {
  return <SheetTrigger asChild>{children}</SheetTrigger>
}

function Content({
  children,
  className,
  handleClassName,
  overlayClassName,
  variant = 'default',
  showCloseButton = false,
  showOverlay = true,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  container,
}: {
  children: React.ReactNode
  className?: string
  handleClassName?: string
  overlayClassName?: string
  variant?: 'default' | 'full-height' | 'transparent'
  showCloseButton?: boolean
  showOverlay?: boolean
  'aria-labelledby'?: string
  'aria-describedby'?: string
  container?: HTMLElement | null
}) {
  return (
    <SheetContent
      direction="bottom"
      showOverlay={showOverlay}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      container={container}
      className={tw(
        'max-h-[calc(100dvh-var(--top-app-bar-height))]',
        variant === 'full-height' && 'h-[calc(100dvh-var(--top-app-bar-height))]',
        variant === 'transparent' &&
          'bg-transparent px-0 [&>.content-container]:px-0 [&>.content-container]:pb-0',
        className,
      )}
      handleClassName={handleClassName}
      showCloseButton={showCloseButton}
      overlayClassName={tw(variant === 'full-height' && '!backdrop-blur-0', overlayClassName)}
    >
      <SheetContentBoundary>{children}</SheetContentBoundary>
    </SheetContent>
  )
}

function Screen({ name, children }: { name: string; children: React.ReactNode }) {
  const { active, registerScreen } = useBottomSheetNavigationInternal()

  React.useLayoutEffect(() => {
    registerScreen(name)
  }, [name, registerScreen])

  const isActive = active === name

  return (
    <section
      hidden={!isActive}
      inert={!isActive ? true : undefined}
      className={tw(isActive && 'flex h-full min-h-0 flex-1 flex-col')}
    >
      <BottomSheetScreenContext.Provider value={name}>{children}</BottomSheetScreenContext.Provider>
    </section>
  )
}

function Title({ children }: { children: React.ReactNode }) {
  return <h2 className="typography-20 font-semibold text-gray-50">{children}</h2>
}

function Header({ children }: { children: React.ReactNode }) {
  const navigation = React.useContext(BottomSheetNavigationContext)
  const screenName = React.useContext(BottomSheetScreenContext)
  const canGoBack = navigation?.canGoBack ?? false
  const showBack = canGoBack && (screenName === null || screenName === navigation?.active)

  return (
    <div className="flex items-center gap-2 px-4 pt-5 pb-2">
      {showBack ? (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          iconOnly
          aria-label="Back"
          onClick={() => navigation?.pop()}
        >
          <ChevronLeftIcon className="size-4 text-gray-100" />
        </Button>
      ) : null}
      <div className="flex min-w-0 flex-1 items-center justify-between">{children}</div>
    </div>
  )
}

function Close({ className }: { className?: string }) {
  return (
    <SheetClose asChild className={tw('absolute right-4 top-4', className)}>
      <Button iconOnly size="xs" variant="ghost" aria-label="Close">
        <XCloseIcon className="size-4 text-gray-100" />
      </Button>
    </SheetClose>
  )
}

export const BottomSheet = {
  Root,
  Trigger,
  Content,
  Screen,
  Title,
  Header,
  Close,
  useNavigation: useBottomSheetNavigation,
}
